import { useNavigate } from "react-router-dom";
import { FlaskConical, Landmark } from "lucide-react";
import { useApp } from "../lib/AppContext";
import { useFetch } from "../lib/useFetch";
import { rm, pnlClass, ts } from "../lib/api";
import { Panel } from "./common";

const seg = (on, live) => `mono flex items-center gap-1 px-2.5 py-1 text-[10px] font-bold transition-colors duration-150 ${on ? (live ? "bg-red-600 text-white" : "bg-amber-500 text-white") : "text-slate-500 hover:bg-[#F1F5F9]"}`;

export function ModeSwitch() {
  const { view, setView } = useApp();
  const nav = useNavigate();
  const go = (v) => { setView(v); nav("/"); };
  return (
    <div className="flex overflow-hidden rounded-sm border border-[#E2E8F0]" data-testid="mode-badge" data-mode={view}>
      <button className={seg(view === "demo")} data-testid="mode-switch-demo" onClick={() => go("demo")} title="Show the DEMO (Moomoo paper) account"><FlaskConical className="h-3 w-3" />DEMO</button>
      <button className={seg(view === "live", true)} data-testid="mode-switch-live" onClick={() => go("live")} title="Show the LIVE (Moomoo MY, real money) account"><Landmark className="h-3 w-3" />LIVE</button>
    </div>
  );
}

export function ModeBanner() {
  const { overview, view } = useApp();
  if (!overview) return null;
  const tradingLive = overview.settings.trading_mode === "live";
  const live = view === "live";
  return (
    <div data-testid="mode-banner" className={`mono flex flex-wrap items-center justify-center gap-x-3 px-3 py-1 text-[10px] font-semibold tracking-wider ${live ? "bg-red-600 text-white" : "bg-amber-100 text-amber-800"}`}>
      <span>{live ? "VIEWING LIVE ACCOUNT · Moomoo MY · REAL MONEY · cash-only 1x" : "VIEWING DEMO ACCOUNT · Moomoo Paper · virtual money"}</span>
      <span data-testid="mode-banner-ai" className="opacity-80">AI trades on: {tradingLive ? "LIVE" : "DEMO"}{!tradingLive && live ? " (LIVE trading locked)" : ""}{live !== tradingLive ? " · other pages show the AI's account" : ""}</span>
    </div>
  );
}

function AccCard({ a, active, testid }) {
  const live = a.real_money;
  return (
    <div data-testid={testid} className={`rounded-sm border p-3 ${active ? (live ? "border-red-500 bg-red-50" : "border-amber-400 bg-amber-50") : "border-[#E2E8F0] bg-white"}`}>
      <div className="flex items-center justify-between">
        <span className={`mono rounded-sm px-1.5 py-0.5 text-[10px] font-bold ${live ? "bg-red-600 text-white" : "bg-amber-500 text-white"}`}>{a.label}</span>
        <span className="mono text-[10px] text-slate-500">{active ? "ACTIVE" : "inactive"} · {a.connected ? "connected" : "not connected"}</span>
      </div>
      <div className="mt-2 text-xs text-slate-600">{a.broker}{a.account ? ` · ${a.account}` : ""}</div>
      {live ? (
        <>
          <div className="mono mt-1 text-xl text-slate-900" data-testid={`${testid}-cash`}>{a.cash_myr != null ? rm(a.cash_myr) : "—"} <span className="text-[10px] text-slate-500">MYR cash</span></div>
          <div className="mono text-[10px] text-slate-500">Total assets {a.total_assets_myr != null ? rm(a.total_assets_myr) : "—"} · synced {a.synced_at ? ts(a.synced_at) : "never"} · {a.policy}</div>
        </>
      ) : (
        <>
          <div className="mono mt-1 text-xl text-slate-900" data-testid={`${testid}-equity`}>{rm(a.equity)} <span className="text-[10px] text-slate-500">equity</span></div>
          <div className="mono text-[10px] text-slate-500">Cash {rm(a.cash)} · today <span className={pnlClass(a.daily_pnl)}>{rm(a.daily_pnl)}</span> · {a.open_positions} open</div>
        </>
      )}
    </div>
  );
}

export function AccountsOverview() {
  const { data } = useFetch("/accounts/overview", 30000);
  if (!data) return null;
  return (
    <Panel title="Accounts" className="mb-3" testid="accounts-overview">
      <div className="grid gap-3 md:grid-cols-2">
        <AccCard a={data.demo} active={data.active === "demo"} testid="account-card-demo" />
        <AccCard a={data.live} active={data.active === "live"} testid="account-card-live" />
      </div>
      <p className="mt-2 text-[10px] text-slate-500">Orders, positions and P&L are recorded separately for each account. Figures elsewhere on this page belong to the ACTIVE account.</p>
    </Panel>
  );
}
